import { Component, OnInit, inject } from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from "@angular/forms";
import { ResourceModel } from "@app/models/rest/resource";
import { RosterAssignmentStore } from "@app/services/store/roster/roster-assignment-store";
import { RosterStore } from "@app/services/store/roster/roster-store";
import { SubDivisionStore } from "@app/services/store/roster/sub-division-store";
import { finalize } from "rxjs/operators";
import { SubDivision } from "@app/models/rest/roster/sub-division";
import { RosterAssignment } from "@app/models/rest/roster/roster-assignment";
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions
} from "@angular/material/dialog";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { Roster } from "@app/models/rest/roster/roster";
import { CdkScrollable } from "@angular/cdk/scrolling";
import {
  MatFormField,
  MatLabel,
  MatSuffix
} from "@angular/material/form-field";
import { MatSelect } from "@angular/material/select";
import { MatOption } from "@angular/material/core";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatInput } from "@angular/material/input";
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker
} from "@angular/material/datepicker";
import { MatButton } from "@angular/material/button";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "app-add-resource-assignment-dialog",
  templateUrl: "./add-assignment-dialog.component.html",
  styleUrls: ["./add-assignment-dialog.component.scss"],
  imports: [
    MatDialogTitle,
    FormsModule,
    ReactiveFormsModule,
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatProgressSpinner,
    MatSuffix,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatDialogActions,
    MatButton,
    TranslatePipe
  ]
})
export class AddResourceAssignmentDialogComponent implements OnInit {
  rosterStore = inject(RosterStore);
  subdivisionStore = inject(SubDivisionStore);
  assignmentStore = inject(RosterAssignmentStore);
  serviceErrorHandler = inject(ServiceErrorHandlerService);
  dialogRef =
    inject<MatDialogRef<AddResourceAssignmentDialogComponent>>(MatDialogRef);
  data = inject<{
    resource: ResourceModel;
  }>(MAT_DIALOG_DATA);

  assignmentForm: UntypedFormGroup = new UntypedFormGroup({
    startDate: new UntypedFormControl("", Validators.required),
    endDate: new UntypedFormControl(""),
    roster: new UntypedFormControl("", Validators.required),
    subDivision: new UntypedFormControl("", Validators.required)
  });

  rosters: Roster[] = [];
  subdivisions: SubDivision[] = [];
  isLoadingRosters: boolean = false;
  isLoadingSubdivisions: boolean = false;

  displayRosterFn(roster: Roster) {
    if (roster) {
      return roster.name;
    }
  }

  displaySubDivisionFn(subDivision: SubDivision) {
    if (subDivision) {
      return subDivision.name;
    }
  }

  ngOnInit() {
    // Load the rosters
    this.isLoadingRosters = true;
    this.rosterStore
      .advancedSearchObservable({}, [])
      .pipe(finalize(() => (this.isLoadingRosters = false)))
      .subscribe(
        (result) => {
          this.rosters = result.resources;
        },
        (error) => {
          this.serviceErrorHandler.handleError(error);
        }
      );

    // Listen for roster changes to trigger subdivision loading
    this.assignmentForm.get("roster").valueChanges.subscribe((roster) => {
      this.assignmentForm.patchValue({ subDivision: null });

      if (roster && roster.id) {
        this.loadSubdivisions(roster.id);
      } else {
        this.subdivisions = [];
      }
    });
  }

  loadSubdivisions(rosterId: string) {
    this.isLoadingSubdivisions = true;
    this.subdivisionStore
      .advancedSearchObservable({}, [
        {
          key: "roster.id",
          operation: "Equals",
          value: rosterId
        }
      ])
      .pipe(finalize(() => (this.isLoadingSubdivisions = false)))
      .subscribe(
        (result) => {
          this.subdivisions = result.resources;
        },
        (error) => {
          this.serviceErrorHandler.handleError(error);
        }
      );
  }

  submit() {
    let rosterAssignment = new RosterAssignment();
    rosterAssignment.startDate = this.assignmentForm.value.startDate;
    rosterAssignment.endDate = this.assignmentForm.value.endDate;
    rosterAssignment.resource = this.data.resource;
    rosterAssignment.subDivision = this.assignmentForm.value.subDivision;
    // The roster is associated through the subdivision

    this.assignmentStore.create(rosterAssignment).subscribe(
      () => {
        this.dialogRef.close(true);
      },
      (error) => {
        this.serviceErrorHandler.handleError(error);
      }
    );
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
